import type { TFunction } from 'i18next';

import type { ReportPreferences } from '@/app/reports/hooks/useReportPreferences';
import type { ReportLibraryRow } from '@/app/reports/types/report-library-row';
import { brandingConfig } from '@/config/branding';

type CatalogItem = {
  id: string;
  category: string;
  titleKey: string;
  descriptionKey: string;
  visualizationPlan: {
    analysisFocus: string;
    visualizations: unknown[];
  };
};

const toReportRow = (
  item: CatalogItem,
  prefs: ReportPreferences,
  t: TFunction<'reports'>,
): ReportLibraryRow => ({
  id: item.id,
  category: item.category,
  title: t(item.titleKey),
  description: t(item.descriptionKey),
  analysisFocus: item.visualizationPlan.analysisFocus,
  visualizationCount: item.visualizationPlan.visualizations.length,
  lastViewedAt: prefs.lastViewed[item.id] ?? null,
  createdBy: brandingConfig.appName,
  isPinned: prefs.pinned.includes(item.id),
});

const matchesSearch = (row: ReportLibraryRow, query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return (
    row.title.toLowerCase().includes(normalized) ||
    row.description.toLowerCase().includes(normalized) ||
    row.id.toLowerCase().includes(normalized)
  );
};

export const mapCatalogToRows = (
  catalog: CatalogItem[],
  prefs: ReportPreferences,
  t: TFunction<'reports'>,
  search?: string,
): ReportLibraryRow[] =>
  catalog
    .map((item) => toReportRow(item, prefs, t))
    .filter((row) => matchesSearch(row, search ?? ''));
